import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { baseUrl } from "../app/api/apiSlice";

import {
  Container,
  Header,
  Button,
  Icon,
  Card,
  Image,
  Grid,
  Segment,
  Statistic,
  Loader,
  Dimmer,
  Table,
  Label
} from "semantic-ui-react";
import { PAGE_SIZE } from "../constants";
import './SharedCard.css';
import './LandingPage.css'; // Import modern styles
import UserHome from "./UserHome";
import StaffHome from "./StaffHome";
import { BRAND } from '../brand';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement
);

const featureList = [
  {
    title: "Realistic Scenarios",
    description:
      "Practice with a wide range of realistic customer interactions to improve your problem-solving skills.",
    icon: "users",
  },
  {
    title: "Instant Feedback",
    description:
      "Receive instant feedback on your performance, helping you identify areas for improvement.",
    icon: "comment",
  },
  {
    title: "Customizable",
    description:
      "Tailor the training scenarios to best suit your store's specific needs and requirements.",
    icon: "cogs",
  },
  {
    title: "Progress Tracking",
    description:
      "Monitor your progress over time and see how your skills improve with each session.",
    icon: "chart line",
  },
];

const SFIcons = {
  "users": (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  ),
  "comment": (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
    </svg>
  ),
  "cogs": (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
      <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  ),
  "chart line": (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h14.25M6.75 12.75l3-3.75 4.5 5.25 6-7.5" />
    </svg>
  ),
};


const LandingPage = () => {
  const navigate = useNavigate();

  const hasToken = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const partner = useSelector((state) => state.auth.partner);

  if (hasToken) {
    if (user?.is_staff || user?.is_contractor_admin) {
      return <StaffHome user={user} navigate={navigate} />;
    }
    return <UserHome user={user} navigate={navigate} />;
  }

  return (
    <div style={{ background: '#fbfbfd', minHeight: '100vh' }}>
      <div className="landing-hero">
        <Container text>
          <Header as="h1" className="landing-title">
            {BRAND}
          </Header>
          <Header as="h2" className="landing-subtitle">
            AI Roleplay and Evaluation Solution
          </Header>
          
          <p style={{ fontSize: '1.2em', color: '#424245', marginBottom: '2.5em', lineHeight: '1.6' }}>
            Our solution uses AI (LLM) to simulate customer interactions and
            allows employees to practice role-playing scenarios that are specific
            to their job. The solution also provides automated evaluation of the
            employee's performance based on evaluation criteria provided by
            Client.
          </p>
          
          <Button
            className="modern-button"
            size="huge"
            onClick={() => {
              if (!hasToken) {
                navigate("login");
              } else {
                navigate(`customerpersonaslist?page=1&page_size=${PAGE_SIZE}`);
              }
            }}
          >
            {hasToken ? "Browse Data" : "Get Started"}
            <Icon name="right arrow" />
          </Button>
        </Container>
      </div>

      <Container style={{ paddingBottom: '5em' }}>
        <Header
          as="h2"
          textAlign="center"
          style={{ 
            fontSize: '2.5em', 
            marginBottom: '1.5em',
            fontWeight: '700'
          }}
        >
          Key Features
        </Header>

        <Grid stackable columns={2} spacing={4}>
          {featureList.map((feature, index) => (
            <Grid.Column key={index} style={{ marginBottom: '2em' }}>
              <div className="feature-card" style={{ padding: '2.5em' }}>
                <div className="feature-icon-wrapper" style={{ 
                  background: index % 2 === 0 ? '#007AFF' : '#5856D6',
                  borderRadius: '50%'
                }}>
                  {SFIcons[feature.icon] ? (
                    SFIcons[feature.icon]({
                      style: { width: '32px', height: '32px', color: 'white' }
                    })
                  ) : (
                    <Icon name={feature.icon} size="large" style={{ color: 'white', margin: 0 }} />
                  )}
                </div>
                <Header as="h3" style={{ fontSize: '1.5em', marginTop: '0.5em' }}>{feature.title}</Header>
                <p style={{ fontSize: '1.1em', color: '#6e6e73', lineHeight: '1.6' }}>
                  {feature.description}
                </p>
              </div>
            </Grid.Column>
          ))}
        </Grid>
      </Container>
      {/* <Footer /> */}
    </div>
  );
};

export default LandingPage;
